import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Box, Button, TextField, Select, MenuItem, FormControl, InputLabel, 
  Typography, Alert, CircularProgress, Paper, Card, CardContent, Divider 
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import './Booking.css';

const monthHash = {
  1: 'JAN', 2: 'FEB', 3: 'MAR', 4: 'APR', 5: 'MAY', 6: 'JUN',
  7: 'JUL', 8: 'AUG', 9: 'SEP', 10: 'OCT', 11: 'NOV', 12: 'DEC'
};

const formatDateToHuman = (d) => {
  if (!d) return '';
  const dateObj = new Date(d);
  if (isNaN(dateObj.getTime())) return d;
  const day = dateObj.getDate();
  const month = monthHash[dateObj.getMonth() + 1];
  const year = dateObj.getFullYear();
  return `${day}-${month}-${year}`;
};

export default function AccommodationBooking() {
  const [searchParams] = useSearchParams();
  const autoApproved = searchParams.get('approval') === 'auto';

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  // Form State
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [startDate, setStartDate] = useState(todayStr);
  const [bookingDuration, setBookingDuration] = useState(1);
  const [endDate, setEndDate] = useState('');
  const [checkInTime, setCheckInTime] = useState(
    `${String(today.getHours()).padStart(2, '0')}:${String(today.getMinutes()).padStart(2, '0')}`
  );
  const [govtId, setGovtId] = useState('');
  const [emergencyContactInfo, setEmergencyContactInfo] = useState('');
  const [purposeSelected, setPurposeSelected] = useState('Spiritual Tour / Vrindavan Yatra');

  const [photoFileUrl, setPhotoFileUrl] = useState('');
  const [photoFileName, setPhotoFileName] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);

  const [aadharFileUrl, setAadharFileUrl] = useState('');
  const [aadharFileName, setAadharFileName] = useState('');
  const [aadharUploading, setAadharUploading] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [bookingSuccess, setBookingSuccess] = useState(false);

  // Auto update end date based on start date & duration
  useEffect(() => {
    if (!startDate) return;
    const startObj = new Date(startDate);
    if (isNaN(startObj.getTime())) return;
    const endObj = new Date(startObj);
    endObj.setDate(endObj.getDate() + Number(bookingDuration));
    setEndDate(endObj.toISOString().split('T')[0]);
  }, [startDate, bookingDuration]);

  // File Upload Helper
  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (type === 'photo') {
      setPhotoFileName(file.name);
      setPhotoUploading(true);
    } else {
      setAadharFileName(file.name);
      setAadharUploading(true);
    }

    try {
      const backendUrl = window.BACKEND_BASE_URL || (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await fetch(`${backendUrl}/temp_file_upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_name: file.name,
          file: b64
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (type === 'photo') {
          setPhotoFileUrl(data.url || data.file_url || URL.createObjectURL(file));
        } else {
          setAadharFileUrl(data.url || data.file_url || URL.createObjectURL(file));
        }
      } else {
        // Fallback to local Data URL
        const reader = new FileReader();
        reader.onloadend = () => {
          if (type === 'photo') setPhotoFileUrl(reader.result);
          else setAadharFileUrl(reader.result);
        };
        reader.readAsDataURL(file);
      }
    } catch (err) {
      console.error('File upload error fallback to base64:', err);
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'photo') setPhotoFileUrl(reader.result);
        else setAadharFileUrl(reader.result);
      };
      reader.readAsDataURL(file);
    } finally {
      if (type === 'photo') setPhotoUploading(false);
      else setAadharUploading(false);
    }
  };

  // Validations per step
  const validateStep1 = () => {
    if (!startDate || !endDate || !checkInTime) {
      setErrorMsg('Please select valid start date, end date, and check-in time.');
      return false;
    }
    setErrorMsg('');
    return true;
  };

  const validateStep2 = () => {
    if (!name.trim()) {
      setErrorMsg('Please enter Guest Full Name.');
      return false;
    }
    if (!mobileNo || mobileNo.length < 10) {
      setErrorMsg('Please enter a valid 10-digit Mobile Number.');
      return false;
    }
    if (!purposeSelected) {
      setErrorMsg('Please select the Purpose of Visit.');
      return false;
    }
    if (!govtId || govtId.length < 12) {
      setErrorMsg('Please enter a valid 12-digit Aadhaar Card Number.');
      return false;
    }
    if (!emergencyContactInfo.trim()) {
      setErrorMsg('Please enter Emergency Contact Information.');
      return false;
    }
    setErrorMsg('');
    return true;
  };

  const validateStep3 = () => {
    if (!photoFileUrl) {
      setErrorMsg('Please upload Guest Passport Photo.');
      return false;
    }
    if (!aadharFileUrl) {
      setErrorMsg('Please upload Aadhaar Card Document.');
      return false;
    }
    setErrorMsg('');
    return true;
  };

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateStep3()) return;
    setStep((prev) => Math.min(prev + 1, 4));
  };

  const handleBack = () => {
    setErrorMsg('');
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrorMsg('');

    try {
      const backendUrl = window.BACKEND_BASE_URL || (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');
      const payload = {
        name: name.trim(),
        mobile_no: mobileNo.trim(),
        start_date: formatDateToHuman(startDate),
        end_date: formatDateToHuman(endDate),
        check_in_time: checkInTime,
        govt_id: govtId.trim(),
        emergency_contact_info: emergencyContactInfo.trim(),
        purpose: purposeSelected,
        photo_url: photoFileUrl,
        aadhar_url: aadharFileUrl,
        auto_approved: autoApproved
      };

      const response = await fetch(`${backendUrl}/booking_create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (response.ok && (data.status === 'success' || data.success || !data.error)) {
        setBookingSuccess(true);
      } else {
        setErrorMsg(data.error || data.message || 'Failed to submit booking request. Please try again.');
      }
    } catch (err) {
      console.error('Booking creation error:', err);
      setErrorMsg('Network error while creating booking. Please check connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setName('');
    setMobileNo('');
    setGovtId('');
    setEmergencyContactInfo('');
    setPhotoFileUrl('');
    setPhotoFileName('');
    setAadharFileUrl('');
    setAadharFileName('');
    setBookingSuccess(false);
    setErrorMsg('');
  };

  return (
    <div className="booking-container">
      <div className="booking-card">
        {/* Header */}
        <div className="booking-header">
          <Typography variant="h4" component="h2">
            FOLK Vrindavan Accommodation Booking
          </Typography>
          <span className="subtitle">
            {autoApproved ? 'Instant Auto-Approved Booking' : 'Guest Registration & Booking Portal'}
          </span>
        </div>

        {/* Step Indicator */}
        {!bookingSuccess && (
          <div className="step-indicator">
            <div className="step-item">
              <div className={`step-dot ${step === 1 ? 'active' : step > 1 ? 'done' : ''}`}>1</div>
              <span className="step-label">Dates</span>
            </div>
            <div className={`step-line ${step > 1 ? 'done' : ''}`}></div>

            <div className="step-item">
              <div className={`step-dot ${step === 2 ? 'active' : step > 2 ? 'done' : ''}`}>2</div>
              <span className="step-label">Details</span>
            </div>
            <div className={`step-line ${step > 2 ? 'done' : ''}`}></div>

            <div className="step-item">
              <div className={`step-dot ${step === 3 ? 'active' : step > 3 ? 'done' : ''}`}>3</div>
              <span className="step-label">Documents</span>
            </div>
            <div className={`step-line ${step > 3 ? 'done' : ''}`}></div>

            <div className="step-item">
              <div className={`step-dot ${step === 4 ? 'active' : ''}`}>4</div>
              <span className="step-label">Confirm</span>
            </div>
          </div>
        )}

        {/* Error Alert */}
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMsg('')}>
            {errorMsg}
          </Alert>
        )}

        {/* Success View */}
        {bookingSuccess ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 72, color: 'var(--booking-accent)', mb: 2 }} />
            <Typography variant="h5" sx={{ color: 'var(--booking-secondary)', fontWeight: 700, mb: 1 }}>
              Booking Request Submitted Successfully!
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, maxWidth: 480, mx: 'auto' }}>
              Your accommodation request has been recorded. Please submit a security deposit of Rs. 300/- at check-in time (refundable at check-out).
            </Typography>

            <Button variant="contained" color="primary" onClick={resetForm} sx={{ borderRadius: 3, px: 4, py: 1.2 }}>
              Book Another Accommodation
            </Button>
          </Box>
        ) : (
          <Box component="form" noValidate>
            {/* Step 1: Dates & Duration */}
            {step === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h6" sx={{ color: 'var(--booking-secondary)', fontWeight: 600 }}>
                  <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Select Stay Schedule
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    label="From Date (Check-in)"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: todayStr }}
                    fullWidth
                  />
                  <TextField
                    label="Duration (Days)"
                    type="number"
                    value={bookingDuration}
                    onChange={(e) => setBookingDuration(Math.max(1, parseInt(e.target.value) || 1))}
                    inputProps={{ min: 1, max: 15 }}
                    fullWidth
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    label="To Date (Check-out)"
                    type="date"
                    value={endDate}
                    InputLabelProps={{ shrink: true }}
                    disabled
                    fullWidth
                    helperText={`Formatted: ${formatDateToHuman(endDate)}`}
                  />
                  <TextField
                    label="Expected Check-in Time"
                    type="time"
                    value={checkInTime}
                    onChange={(e) => setCheckInTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>
              </Box>
            )}

            {/* Step 2: Guest Details */}
            {step === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Typography variant="h6" sx={{ color: 'var(--booking-secondary)', fontWeight: 600 }}>
                  <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Personal & Contact Details
                </Typography>

                <TextField
                  label="Full Name"
                  placeholder="Enter guest full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  required
                />

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    label="Mobile Number"
                    placeholder="10-digit mobile number"
                    value={mobileNo}
                    onChange={(e) => setMobileNo(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    fullWidth
                    required
                  />
                  <FormControl fullWidth required>
                    <InputLabel id="purpose-select-label">Purpose of Visit</InputLabel>
                    <Select
                      labelId="purpose-select-label"
                      value={purposeSelected}
                      label="Purpose of Visit"
                      onChange={(e) => setPurposeSelected(e.target.value)}
                    >
                      <MenuItem value="Spiritual Tour / Vrindavan Yatra">Spiritual Tour / Vrindavan Yatra</MenuItem>
                      <MenuItem value="Volunteering Service">Volunteering Service</MenuItem>
                      <MenuItem value="Temple Visit / Festival">Temple Visit / Festival</MenuItem>
                      <MenuItem value="Personal Visit">Personal Visit</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <TextField
                  label="Aadhaar Card Number (12 Digits)"
                  placeholder="12-digit Aadhaar Number"
                  value={govtId}
                  onChange={(e) => setGovtId(e.target.value.replace(/\D/g, '').slice(0, 12))}
                  fullWidth
                  required
                />

                <TextField
                  label="Emergency Contact Details"
                  placeholder="Contact person name, phone number, address"
                  value={emergencyContactInfo}
                  onChange={(e) => setEmergencyContactInfo(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                  required
                />
              </Box>
            )}

            {/* Step 3: Document Uploads */}
            {step === 3 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h6" sx={{ color: 'var(--booking-secondary)', fontWeight: 600 }}>
                  <BadgeIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Identity Documents Upload
                </Typography>

                {/* Passport Photo */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                    1. Guest Photo (Passport size / Clear face photo) *
                  </Typography>
                  <label htmlFor="photo-upload-input">
                    <input
                      id="photo-upload-input"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileUpload(e, 'photo')}
                    />
                    <div className="upload-dropzone">
                      {photoUploading ? (
                        <CircularProgress size={28} />
                      ) : photoFileUrl ? (
                        <div>
                          <CheckCircleIcon sx={{ color: 'var(--booking-accent)', fontSize: 32 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>Photo Selected: {photoFileName || 'Image uploaded'}</Typography>
                          <img src={photoFileUrl} alt="Photo Preview" className="upload-preview" />
                        </div>
                      ) : (
                        <div>
                          <CloudUploadIcon sx={{ fontSize: 36, color: 'var(--booking-primary)' }} />
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            Click to browse & upload Guest Photo
                          </Typography>
                        </div>
                      )}
                    </div>
                  </label>
                </Box>

                {/* Aadhaar Upload */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                    2. Aadhaar Card Photo / Scan Document *
                  </Typography>
                  <label htmlFor="aadhar-upload-input">
                    <input
                      id="aadhar-upload-input"
                      type="file"
                      accept="image/*,.pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileUpload(e, 'aadhar')}
                    />
                    <div className="upload-dropzone">
                      {aadharUploading ? (
                        <CircularProgress size={28} />
                      ) : aadharFileUrl ? (
                        <div>
                          <CheckCircleIcon sx={{ color: 'var(--booking-accent)', fontSize: 32 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>Aadhaar Selected: {aadharFileName || 'Document uploaded'}</Typography>
                          {aadharFileUrl.startsWith('data:image') || aadharFileUrl.startsWith('http') ? (
                            <img src={aadharFileUrl} alt="Aadhaar Preview" className="upload-preview" />
                          ) : null}
                        </div>
                      ) : (
                        <div>
                          <CloudUploadIcon sx={{ fontSize: 36, color: 'var(--booking-primary)' }} />
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            Click to browse & upload Aadhaar Card
                          </Typography>
                        </div>
                      )}
                    </div>
                  </label>
                </Box>
              </Box>
            )}

            {/* Step 4: Summary & Confirmation */}
            {step === 4 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Typography variant="h6" sx={{ color: 'var(--booking-secondary)', fontWeight: 600 }}>
                  Confirm Booking Details
                </Typography>

                <Paper sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 700 }}>
                    BOOKING SUMMARY
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Guest Name:</strong> {name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Mobile Number:</strong> {mobileNo}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Stay Period:</strong> {formatDateToHuman(startDate)} ({checkInTime}) to {formatDateToHuman(endDate)}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Purpose:</strong> {purposeSelected}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Aadhaar No:</strong> {govtId}
                  </Typography>
                </Paper>

                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <strong>Note:</strong> Security deposit of <strong>Rs. 300/-</strong> is required at check-in (fully refundable upon check-out).
                </Alert>
              </Box>
            )}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: '1px solid #e2e8f0' }}>
              {step > 1 ? (
                <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />}>
                  Back
                </Button>
              ) : (
                <div />
              )}

              {step < 4 ? (
                <Button variant="contained" onClick={handleNext} endIcon={<ArrowForwardIcon />} sx={{ bgcolor: 'var(--booking-primary)' }}>
                  Next Step
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                  sx={{ bgcolor: 'var(--booking-accent)', '&:hover': { bgcolor: 'var(--booking-accent-dark)' } }}
                >
                  {isSubmitting ? 'Submitting...' : 'Confirm & Book Now'}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </div>
    </div>
  );
}
